<template>
  <transition
    :enterActiveClass="$style.alacarteTransitionIn"
    :enterClass="$style.alacarteHidden"
    :leaveActiveClass="cartTransitionOutClass"
    :leaveToClass="$style.alacarteHidden"
  >
    <FloatingFixedBottomCard
      v-show="shouldShowCart"
      :class="$style.bottomCardContainer"
      data-test-id="ALaCarteCart__bottomCardContainer"
    >
      <template v-slot:leftAction>
        <IconButton
          :class="$style.expandCartButton"
          data-test-id="ALaCarteCart__viewCartButton"
          hasNoBackground
          :icon="isMobileCartOpen ? 'chevronUp' : 'chevronDown'"
          text="View Cart"
          @click="handleMobileCartToggle"
        />
      </template>

      <template v-slot:close>
        <IconButton
          data-test-id="ALaCarteCart__closeButton"
          hasNoBackground
          icon="close"
          text="Clear Cart"
          @click="handleClearCartClicked"
        />
      </template>

      <div :class="$style.cardContainer">
        <LatestActionCard
          :class="[$style.card, $style.actionCard]"
          data-test-id="ALaCarteCart__latestActionCard"
        />

        <transition
          :enterActiveClass="$style.cardTransition"
          :enterClass="$style.cardHidden"
          :leaveActiveClass="$style.cardTransition"
          :leaveToClass="$style.cardHidden"
        >
          <!-- This card should be blanked out when the cart is empty,
            but still take up space until the transition finishes -->
          <div
            v-show="hasAddons"
            :class="[$style.card, $style.featuresListCard, { [$style.expanded]: isMobileCartOpen}]"
          >
            <ListOfFeaturesCard
              v-if="hasAddons"
              :class="$style.innerCard"
              data-test-id="ALaCarteCart__listOfFeaturesCard"
            />
          </div>
        </transition>

        <transition
          :enterActiveClass="$style.cardTransition"
          :enterClass="$style.cardHidden"
          :leaveActiveClass="$style.cardTransition"
          :leaveToClass="$style.cardHidden"
        >
          <!-- This card should be blanked out when the cart is empty,
            but still take up space until the transition finishes -->
          <div
            v-show="hasAddons"
            :class="[$style.card]"
          >
            <CheckoutCard
              v-if="hasAddons"
              :class="$style.innerCard"
              data-test-id="ALaCarteCart__checkoutCard"
              v-on="$listeners"
            />
          </div>
        </transition>
      </div>
    </FloatingFixedBottomCard>
  </transition>
</template>

<script>
import {
  IconButton,
} from '@jumpcloud/ui-components';
import {
  eventLocations,
  eventProperties,
} from 'client/GrowthEngineering/utilities/GrowthConstants';
import { mapActions, mapGetters } from 'vuex';
import CheckoutCard from 'client/Pricing/ALaCarte/CheckoutCard.vue';
import EventHelper from 'client/GrowthEngineering/EventHelper';
import FloatingFixedBottomCard from 'client/Pricing/ALaCarte/FloatingFixedBottomCard.vue';
import LatestActionCard from 'client/Pricing/ALaCarte/LatestActionCard.vue';
import ListOfFeaturesCard from 'client/Pricing/ALaCarte/ListOfFeaturesCard.vue';
import SubscriptionConstants from 'client/Payments/pricingMatrix/SubscriptionConstants';

const checkoutModel = 'CheckoutModel';

const {
  pricingPage,
} = eventLocations;

const {
  ProductCodeToSubscriptionType,
  SubscriptionTypes,
} = SubscriptionConstants;

const {
  description,
  location,
  text,
  section,
} = eventProperties;

export default {
  name: 'ALaCarteCart',

  components: {
    CheckoutCard,
    IconButton,
    LatestActionCard,
    ListOfFeaturesCard,
    FloatingFixedBottomCard,
  },

  data() {
    return {
      isClearingCart: false,
      isMobileCartOpen: false,
    };
  },

  computed: {
    ...mapGetters(checkoutModel, {
      hasAddons: 'hasSelectedAddons',
      subscription: 'item',
    }),

    cartTransitionOutClass() {
      return this.isClearingCart
        ? this.$style.alacarteTransitionOut
        : this.$style.alacarteDelayedTransitionOut;
    },

    shouldShowCart() {
      const { productCode } = this.subscription;
      return ProductCodeToSubscriptionType[productCode] === SubscriptionTypes.alaCarte;
    },
  },

  methods: {
    ...mapActions(checkoutModel, [
      'resetItem',
    ]),

    async handleClearCartClicked() {
      this.isClearingCart = true;
      this.resetItem();
      EventHelper.triggerButtonClicked({
        [description]: 'admin has closed the A La Carte cart experience',
        [location]: pricingPage,
        [section]: 'A La Carte cart',
        [text]: 'Clear Cart',
      });

      // Let transitions start with isClearingCart = true,
      // but reset them back so the default behavior happens next time
      await this.$nextTick();
      this.isClearingCart = false;
    },

    async handleMobileCartToggle() {
      this.isMobileCartOpen = !this.isMobileCartOpen;
    },
  },
};
</script>

<style scoped module>
@value (panelSpeed, panelTimingFunction) from '~css/transitions.css';
@value (max-width-medium, max-width-x-large) from '~css/breakpoints.css';

/* Open/Close animations */

.alacarteHidden {
  transform: translateY(100%);
}

.alacarteTransitionIn {
  transition: transform panelSpeed panelTimingFunction;
}

.alacarteDelayedTransitionOut {
  transition: transform panelSpeed panelTimingFunction;
  transition-delay: 1.5s;
}

.alacarteTransitionOut {
  transition: transform panelSpeed panelTimingFunction;
}

@media (max-width: max-width-x-large) {
  /* On smaller screens, a delay no longer makes sense
     due to action card being hidden */
  .alacarteDelayedTransitionOut {
    transition-delay: 0s;
  }
}

.cardTransition {
  transition: flex-grow 300ms panelTimingFunction;
}

/* Default Component styling */

.bottomCardContainer.bottomCardContainer {
  background-color: var(--jcWhite);
}

.card {
  flex-basis: 0;
  flex-grow: 1;
  height: 100%;
}

.cardHidden.cardHidden {
  flex-grow: 0.01;
}

.innerCard {
  background-color: white;
  height: 100%;
}

.cardContainer {
  display: flex;
  gap: var(--jc-spacer);
  height: 15rem;
}

.expandCartButton {
  display: none !important;
}

/* For Tablets / Small Screens */

@media (max-width: max-width-x-large) {
  .actionCard {
    display: none;
  }
}

/* For Mobile */

@media (max-width: max-width-medium) {
  .featuresListCard {
    display: none;
  }

  .featuresListCard.expanded {
    display: block;
    max-height: 15rem;
  }

  .cardContainer {
    flex-direction: column;
    height: inherit;
  }

  .expandCartButton {
    display: inherit !important;
  }
}

</style>
